import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Box, Typography, CircularProgress, TextField, Button } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReLoginModal from '../../ReLoginModal';

interface LicenseModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (licenseData: {
    _id: string;
    licenseNumber: string;
    licenseOwner: string;
    destination: string;
    documentUrl?: string;
    programId?: string;
    truckNumber?: string;
    quantity?: string;
    product?: { id: string; name: string; selling: number };
    exDepotPrice?: string;
    issuedDate?: string;
    expiryDate?: string;
  }) => void;
}

interface License {
  _id: string;
  licenseNumber: string;
  licenseOwner: string;
  destination: string;
  issuedDate?: string;
  expiryDate?: string;
  documentUrl?: string;
  programId?: string;
  truckNumber?: string;
  quantity?: string;
  product?: { id: string; name: string; selling: number };
  exDepotPrice?: string;
}

type ApiError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const LicenseSelectModal: React.FC<LicenseModalProps> = ({ open, onClose, onSelect }) => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reLogin, setReLogin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchLicenses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setReLogin(true);
      toast.error('Session expired, please log in again.');
      setIsLoading(false);
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    try {
      const response = await axios.get('https://apibiz.oneflaretech.com/api/license', { headers });
      // console.log('Fetched licenses:', response.data.data.licenses);
      setLicenses(response.data.data.licenses || []);
    } catch (error) {
      const err = error as ApiError;
      const errorMessage = err.response?.data?.message || 'Failed to load licenses';
      setError(errorMessage);
      if (errorMessage.includes('jwt expired') || errorMessage.includes('jwt malformed')) {
        setReLogin(true);
        toast.error('Session expired, please log in again.');
      } else {
        toast.error(errorMessage);
      }
      console.error('Error fetching licenses:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchLicenses();
    }
  }, [open, fetchLicenses]);

  const handleSelectLicense = (license: License) => {
    if (!license.licenseOwner) {
      toast.error('Selected license has no associated customer.');
      return;
    }
    onSelect({
      _id: license._id,
      licenseNumber: license.licenseNumber,
      licenseOwner: license.licenseOwner,
      destination: license.destination,
      documentUrl: license.documentUrl,
      programId: license.programId,
      truckNumber: license.truckNumber,
      quantity: license.quantity,
      product: license.product,
      exDepotPrice: license.exDepotPrice,
      issuedDate: license.issuedDate,
      expiryDate: license.expiryDate,
    });
    onClose();
  };

  const filteredLicenses = licenses.filter(license =>
    license.licenseOwner.toLowerCase().includes(searchQuery.toLowerCase()) ||
    license.licenseNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {reLogin && <ReLoginModal />}
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            boxShadow: 24,
            p: 4,
            minWidth: 400,
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            Select License
          </Typography>
          <TextField
            label="Search by License Number or Customer"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2, maxWidth: '250px' }}
            InputProps={{
              endAdornment: <Button onClick={() => {}} sx={{ minWidth: 'auto', color: 'black' }}>🔍</Button>,
            }}
          />
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : error ? (
            <Typography color="error" align="center">{error}</Typography>
          ) : filteredLicenses.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid black' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', fontWeight: 'bold', borderBottom: '1px solid black' }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: 'black' }}>License Number</th>
                  <th style={{ padding: '8px', textAlign: 'left', color: 'black' }}>License owner</th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenses.map((license) => (
                  <tr
                    key={license._id}
                    style={{ height: '40px', borderBottom: '1px solid black', cursor: 'pointer'}}
                    onClick={() => handleSelectLicense(license)}
                  >
                    <td style={{ padding: '4px', color: 'black' }}>{license.licenseNumber}</td>
                    <td style={{ padding: '4px', color: 'black' }}>{license.licenseOwner || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Typography align="center">No licenses available</Typography>
          )}
          <Button variant="contained" color="primary" onClick={onClose} sx={{ mt: 2 }}>
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default LicenseSelectModal;